import React, { useState, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  Shield,
  MapPin,
  Car,
  Clock,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  FileText,
  Calendar,
  CreditCard,
  Users,
  Heart,
  Home,
  Ship,
  Zap,
  Plane,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Images from "../../utils/Images";
import PolicyDatesInfo from "./PolicyDatesInfo";
import { formatMoney } from "../../utils/formatMoney";

// Helper function to truncate text
const TruncatedText = ({ text, maxLength = 200, className = "" }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!text) return null;
  
  const shouldTruncate = text.length > maxLength;
  const displayText = isExpanded || !shouldTruncate ? text : `${text.slice(0, maxLength)}...`;
  
  return (
    <div className={className}>
      <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">
        {displayText}
      </p>
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex gap-1 items-center mt-2 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
        >
          {isExpanded ? (
            <>
              <ChevronUp size={14} />
              <span>Show Less</span>
            </>
          ) : (
            <>
              <ChevronDown size={14} />
              <span>Read More</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

const Row = ({ leftLabel, leftValue, rightLabel, rightValue }) => (
  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
    <div>
      <p className="text-sm font-semibold text-gray-900">{leftLabel}</p>
      <p className="mt-1 text-sm text-gray-700">{leftValue}</p>
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-900">{rightLabel}</p>
      <p className="mt-1 text-sm text-gray-700">{rightValue}</p>
    </div>
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    pink: "bg-pink-50 text-pink-700 border-pink-200",
    red: "bg-red-50 text-red-700 border-red-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
    teal: "bg-teal-50 text-teal-700 border-teal-200",
  };
  
  return (
    <div className="flex gap-3 items-center mb-3">
      <div className={`grid flex-shrink-0 place-items-center w-9 h-9 rounded-xl border ${colorClasses[color] || colorClasses.blue} shadow-sm`}>
        <Icon size={17} />
      </div>
      <div className="flex-1">
        <h3 className="text-base font-bold text-gray-900 leading-tight">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
};

// Insurance type style mapping
const insuranceTypeStyleMap = {
  travel_insurance: {
    color: "blue",
    icon: Plane,
    gradient: "from-blue-500 to-indigo-600",
    headerGradient: "from-blue-600 to-indigo-700",
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700",
  },
  life_insurance: {
    color: "indigo",
    icon: Shield,
    gradient: "from-indigo-500 to-blue-600",
    headerGradient: "from-indigo-600 to-blue-700",
    bg: "bg-indigo-50",
    border: "border-indigo-200",
    text: "text-indigo-700",
  },
  general_insurance: {
    color: "purple",
    icon: FileText,
    gradient: "from-purple-500 to-violet-600",
    headerGradient: "from-purple-600 to-violet-700",
    bg: "bg-purple-50",
    border: "border-purple-200",
    text: "text-purple-700",
  },
  health_insurance: {
    color: "pink",
    icon: Heart,
    gradient: "from-pink-500 to-rose-600",
    headerGradient: "from-pink-600 to-rose-700",
    bg: "bg-pink-50",
    border: "border-pink-200",
    text: "text-pink-700",
  },
  home_insurance: {
    color: "purple",
    icon: Home,
    gradient: "from-purple-500 to-violet-600",
    headerGradient: "from-purple-600 to-violet-700",
    bg: "bg-purple-50",
    border: "border-purple-200",
    text: "text-purple-700",
  },
  accident_insurance: {
    color: "red",
    icon: AlertTriangle,
    gradient: "from-red-500 to-rose-600",
    headerGradient: "from-red-600 to-rose-700",
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
  },
  fire_insurance: {
    color: "orange",
    icon: Zap,
    gradient: "from-orange-500 to-red-600",
    headerGradient: "from-orange-600 to-red-700",
    bg: "bg-orange-50",
    border: "border-orange-200",
    text: "text-orange-700",
  },
  marine_insurance: {
    color: "cyan",
    icon: Ship,
    gradient: "from-cyan-500 to-blue-600",
    headerGradient: "from-cyan-600 to-blue-700",
    bg: "bg-cyan-50",
    border: "border-cyan-200",
    text: "text-cyan-700",
  },
  liability_insurance: {
    color: "teal",
    icon: Shield,
    gradient: "from-teal-500 to-green-600",
    headerGradient: "from-teal-600 to-green-700",
    bg: "bg-teal-50",
    border: "border-teal-200",
    text: "text-teal-700",
  },
};

const PolicyViewModal = ({ open, onClose, policy }) => {
  // Memoize data extraction to avoid creating new objects on every render
  const purchasedData = useMemo(() => policy?.purchased_policy_data || {}, [policy]);
  const insurancePolicy = useMemo(() => policy?.insurance_policy || {}, [policy]);
  const customer = useMemo(() => policy?.customer || {}, [policy]);

  // Determine insurance type
  const insuranceType = useMemo(() => {
    if (purchasedData?.insurance_type) {
      return purchasedData.insurance_type;
    }
    return insurancePolicy?.short_description || "travel_insurance";
  }, [purchasedData, insurancePolicy]);

  // Determine policy type
  const policyType = useMemo(() => {
    if (purchasedData?.policy_type) {
      return purchasedData.policy_type;
    }
    return insurancePolicy?.policy_type;
  }, [purchasedData, insurancePolicy]);

  // Get style based on insurance type
  const getInsuranceStyles = useMemo(() => {
    if (insuranceType === "general_insurance" && policyType) {
      return insuranceTypeStyleMap[policyType] || insuranceTypeStyleMap.general_insurance;
    }
    return insuranceTypeStyleMap[insuranceType] || insuranceTypeStyleMap.travel_insurance;
  }, [insuranceType, policyType]);

  const styles = getInsuranceStyles;
  const InsuranceIcon = styles.icon;

  // Determine if it's travel, life, or general insurance
  const isTravelInsurance = useMemo(() => insuranceType === "travel_insurance", [insuranceType]);
  const isTripSecureInsurance = useMemo(() => insuranceType === "tripsecure_insurance", [insuranceType]);
  const isLifeInsurance = useMemo(() => insuranceType === "life_insurance", [insuranceType]);
  const isGeneralInsurance = useMemo(() => insuranceType === "general_insurance", [insuranceType]);

  // Get policy name
  const policyName = useMemo(() => {
    if (isLifeInsurance || isGeneralInsurance) {
      return purchasedData?.product_name || insurancePolicy?.policy_name || "Insurance Policy";
    }
    return insurancePolicy?.policy_name || "TripSecure Policy";
  }, [isLifeInsurance, isGeneralInsurance, purchasedData, insurancePolicy]);

  // Get coverage duration info
  const coverageDuration = useMemo(() => {
    if ((isTravelInsurance || isTripSecureInsurance) && purchasedData?.coverageDuration) {
      return purchasedData.coverageDuration;
    }
    // For okada_rider and daily_commuter, check coverage_durations in insurance_policy
    if (isTripSecureInsurance && (policyType === "okada_rider" || policyType === "daily_commuter") && insurancePolicy?.coverage_durations?.length > 0) {
      return insurancePolicy.coverage_durations[0];
    }
    return null;
  }, [isTravelInsurance, isTripSecureInsurance, purchasedData, policyType, insurancePolicy]);

  // Check if travel_insurance uses string duration format (not coverageDuration object)
  const hasStringDuration = useMemo(() => {
    return isTravelInsurance && purchasedData?.duration && typeof purchasedData.duration === 'string' && !purchasedData?.coverageDuration;
  }, [isTravelInsurance, purchasedData]);

  // Helper to format duration (e.g., "1 monthly" -> "1 month", "2 yearly" -> "2 years")
  const formatDuration = (duration) => {
    if (!duration) return "N/A";
    
    const durationStr = String(duration).toLowerCase();
    
    // Match pattern like "1 monthly", "2 yearly", "3 weekly", etc.
    const match = durationStr.match(/^(\d+)\s*(daily|weekly|monthly|yearly|day|week|month|year)s?$/);
    
    if (!match) return duration; // Return as-is if no match
    
    const value = parseInt(match[1]);
    let unit = match[2];
    
    // Normalize unit names
    const unitMap = {
      'daily': 'day',
      'weekly': 'week', 
      'monthly': 'month',
      'yearly': 'year'
    };
    
    unit = unitMap[unit] || unit;
    
    // Pluralize if value > 1
    if (value > 1) {
      unit = unit + 's';
    }
    
    return `${value} ${unit}`;
  };

  // Format duration label
  const formatDurationLabel = useMemo(() => {
    // For travel_insurance with string duration (e.g., "3 hours")
    if (hasStringDuration) {
      return formatDuration(purchasedData.duration);
    }
    if ((isTravelInsurance || isTripSecureInsurance) && coverageDuration) {
      if (coverageDuration.display_unit && coverageDuration.display_value) {
        const unit = coverageDuration.display_unit;
        const value = coverageDuration.display_value;
        return `${value} ${value === 1 ? unit.slice(0, -1) : unit}`;
      }
      if (coverageDuration.hours) {
        const hours = coverageDuration.hours;
        return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
      }
    }
    if (isLifeInsurance || isGeneralInsurance) {
      return formatDuration(purchasedData?.duration) || "N/A";
    }
    return "N/A";
  }, [isTravelInsurance, isTripSecureInsurance, isLifeInsurance, isGeneralInsurance, coverageDuration, purchasedData, hasStringDuration]);

  // Get price/premium
  const getPrice = useMemo(() => {
    // For travel_insurance with string duration, use premium from purchased_policy_data
    if (hasStringDuration) {
      return purchasedData?.premium || 0;
    }
    if ((isTravelInsurance || isTripSecureInsurance) && coverageDuration) {
      return coverageDuration.price || 0;
    }
    if (isLifeInsurance || isGeneralInsurance) {
      return purchasedData?.premium || 0;
    }
    return 0;
  }, [isTravelInsurance, isTripSecureInsurance, isLifeInsurance, isGeneralInsurance, coverageDuration, purchasedData, hasStringDuration]);

  // Get premium duration (payment term) for Life, General, and Travel Insurance
  const getPremiumDuration = useMemo(() => {
    if (isLifeInsurance || isGeneralInsurance || (isTravelInsurance && hasStringDuration)) {
      const duration = purchasedData?.premium_duration || "monthly";
      // Normalize duration for display (yearly -> year, monthly -> month, etc.)
      const durationMap = {
        'yearly': 'year',
        'monthly': 'month',
        'weekly': 'week',
        'daily': 'day'
      };
      return durationMap[duration.toLowerCase()] || duration;
    }
    return null;
  }, [isLifeInsurance, isGeneralInsurance, isTravelInsurance, hasStringDuration, purchasedData]);

  // Get policy type label
  const getPolicyTypeLabel = useMemo(() => {
    if (isTravelInsurance || isTripSecureInsurance) {
      const typeMap = {
        intercity_travel: "Intercity Travel",
        daily_commuter: "Daily Commuter",
        okada_rider: "Motor Rider",
        taxi_and_bolt: "Taxi & Bolt",
      };
      return typeMap[policyType] || policyType?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || (isTripSecureInsurance ? "TripSecure Insurance" : "Travel Insurance");
    }
    if (isLifeInsurance) {
      return "Life Insurance";
    }
    if (isGeneralInsurance) {
      const subcategoryMap = {
        health_insurance: "Health Insurance",
        home_insurance: "Home Insurance",
        accident_insurance: "Accident Insurance",
        fire_insurance: "Fire Insurance",
        marine_insurance: "Marine Insurance",
        liability_insurance: "Liability Insurance",
      };
      return subcategoryMap[policyType] || "General Insurance";
    }
    return "Insurance Policy";
  }, [isTravelInsurance, isTripSecureInsurance, isLifeInsurance, isGeneralInsurance, policyType]);

  const formatCurrency = useMemo(() => (amount) => formatMoney(amount), []);
  
  const formatPhoneNumber = useMemo(() => (phone) => (phone ? `+${phone}` : "N/A"), []);

  const paymentInfo = useMemo(() => {
    const isActive = policy?.policy_status === null || policy?.policy_status === "active";
    return { 
      label: isActive ? "Active" : "Inactive", 
      color: isActive ? "text-emerald-700" : "text-gray-700", 
      bg: isActive ? "bg-emerald-100" : "bg-gray-100" 
    };
  }, [policy?.policy_status]);

  // Calculate total coverage based on route type
  const totalCoverage = useMemo(() => {
    if (!insurancePolicy.insurance_coverage_list) return 0;

    const routeType = coverageDuration?.route_type;

    return insurancePolicy.insurance_coverage_list.reduce((sum, coverage) => {
      if (coverage.coverage_type && routeType) {
        if (coverage.coverage_type === routeType) {
          return sum + parseFloat(coverage.limit || 0);
        }
        return sum;
      }
      return sum + parseFloat(coverage.limit || 0);
    }, 0);
  }, [insurancePolicy.insurance_coverage_list, coverageDuration?.route_type]);

  if (!policy) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex fixed inset-0 z-50 justify-center items-center p-4"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 backdrop-blur-sm bg-black/40"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden relative z-10 w-full max-w-5xl bg-white rounded-3xl ring-1 ring-gray-200/50 shadow-2xl flex flex-col max-h-[92vh]"
          >
            {/* Compact Fixed Header */}
            <div className={`relative px-6 py-4 bg-gradient-to-br ${styles.headerGradient || styles.gradient} border-b border-white/20 shadow-lg`}>
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 grid place-items-center w-8 h-8 text-white/90 rounded-xl transition-all hover:text-white hover:bg-white/20 backdrop-blur-sm"
              >
                <X size={18} />
              </button>

              {/* Header Content */}
              <div className="flex gap-4 items-start pr-10">
                {/* Icon */}
                <div className="grid flex-shrink-0 place-items-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl shadow-lg ring-2 ring-white/30">
                  <InsuranceIcon size={22} className="text-white" />
                </div>

                {/* Title and Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex gap-3 items-center mb-1.5">
                    <h2 className="text-xl font-bold text-white leading-tight">
                      {policyName}
                    </h2>
                    <div
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${paymentInfo.bg} ${paymentInfo.color} shadow-sm`}
                    >
                      <CreditCard size={11} />
                      {paymentInfo.label}
                    </div>
                  </div>
                  <div className="flex gap-4 items-center flex-wrap">
                    <p className="text-sm font-medium text-white/90">
                      {getPolicyTypeLabel}
                    </p>
                    <span className="text-white/60">•</span>
                    <p className="text-xs font-mono text-white/80">
                      ID: {policy?.insurance_policy_slug?.slice(-8) || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto overscroll-contain p-6 space-y-6 bg-gray-50/50">
              {/* Policy Overview */}
              <div>
                <SectionHeader
                  icon={Shield}
                  title="Policy Overview"
                  subtitle="Basic policy information and details"
                  color={styles.color}
                />
                <div className={`p-5 space-y-4 ${styles.bg} rounded-2xl border ${styles.border} shadow-sm`}>
                  <Row
                    leftLabel="Policy Type"
                    leftValue={getPolicyTypeLabel}
                    rightLabel="Customer Phone"
                    rightValue={formatPhoneNumber(customer?.phone_number)}
                  />
                  <div className="pt-3 border-t border-gray-200/60">
                    <p className="mb-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Description
                    </p>
                    <TruncatedText
                      text={
                        (isLifeInsurance || isGeneralInsurance) 
                          ? purchasedData?.description 
                          : insurancePolicy.description || 
                        insurancePolicy.short_description ||
                            "No description available"
                      }
                      maxLength={200}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <SectionHeader
                  icon={Calendar}
                  title="Purchase & Validity"
                  subtitle="When this policy was bought and how long it remains valid"
                  color={styles.color}
                />
                <div className={`p-5 ${styles.bg} rounded-2xl border ${styles.border} shadow-sm`}>
                  <PolicyDatesInfo policy={policy} />
                </div>
              </div>

              {/* Package/Product Details for Life and General Insurance */}
              {(isLifeInsurance || isGeneralInsurance) && purchasedData && (
                <div>
                  <SectionHeader
                    icon={DollarSign}
                    title="Package Details"
                    subtitle="Your purchased package information"
                    color={styles.color}
                  />
                  <div className={`p-5 ${styles.bg} rounded-2xl border ${styles.border} shadow-sm`}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                        <p className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Product Name
                        </p>
                        <p className="text-base font-bold text-gray-900 leading-tight">
                          {purchasedData.product_name || "N/A"}
                        </p>
                      </div>
                      <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                        <p className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Premium
                        </p>
                        <div className="flex flex-col">
                          <p className={`text-base font-bold ${styles.text}`}>
                            {formatCurrency(getPrice)}
                          </p>
                          {getPremiumDuration && (
                            <p className="text-xs text-gray-500 capitalize mt-0.5">
                              per {getPremiumDuration}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                        <p className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Duration
                        </p>
                        <p className="text-base font-bold text-gray-900">
                          {formatDurationLabel}
                        </p>
                      </div>
                      <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                        <p className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Policy Type
                        </p>
                        <p className="text-base font-bold text-gray-900">
                          {getPolicyTypeLabel}
                        </p>
                      </div>
                    </div>
                    {purchasedData.description && (
                      <div className="mt-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                        <p className="mb-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Package Description
                        </p>
                        <TruncatedText
                          text={purchasedData.description}
                          maxLength={200}
                          className="text-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Coverage Details */}
              {((isLifeInsurance || isGeneralInsurance || hasStringDuration) 
                ? purchasedData?.insurance_coverage_list?.length > 0 
                : insurancePolicy.insurance_coverage_list?.length > 0) && (
              <div>
                <SectionHeader
                  icon={DollarSign}
                  title="Coverage Details"
                  subtitle="Insurance coverage limits and benefits"
                    color={styles.color}
                />
                  <div className={`p-5 mb-4 ${styles.bg} rounded-2xl border ${styles.border} shadow-sm`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className={`text-sm font-bold ${styles.text} mb-1`}>
                          {isLifeInsurance || isGeneralInsurance || hasStringDuration ? "Package Coverage" : "Total Coverage Amount"}
                        </h4>
                        <p className={`text-xs ${styles.text} opacity-80`}>
                          {coverageDuration?.route_type && (
                            <span className="capitalize">
                              {coverageDuration.route_type} route coverage
                            </span>
                          )}
                          {!coverageDuration?.route_type && "Comprehensive coverage"}
                        </p>
                      </div>
                      <span className={`text-3xl font-bold ${styles.text}`}>
                        {formatCurrency(isLifeInsurance || isGeneralInsurance || hasStringDuration ? getPrice : totalCoverage)}
                      </span>
                    </div>
                  </div>
                <div className="space-y-4">
                  {((isLifeInsurance || isGeneralInsurance || hasStringDuration) 
                    ? purchasedData?.insurance_coverage_list 
                    : insurancePolicy.insurance_coverage_list)
                    ?.filter((coverage) => {
                      const routeType = coverageDuration?.route_type;
                      if (coverage.coverage_type && routeType) {
                        return coverage.coverage_type === routeType;
                      }
                        return true;
                    })
                    .map((coverage, index) => (
                      <div
                        key={index}
                        className="p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-200/60 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start mb-2.5">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-emerald-900 mb-1.5">
                              {coverage.title}
                            </h4>
                            {coverage.coverage_type && (
                              <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-emerald-200/80 text-emerald-800 rounded-lg capitalize">
                                {coverage.coverage_type}
                              </span>
                            )}
                          </div>
                          <span className="text-xl font-bold text-emerald-700 ml-3 flex-shrink-0">
                            {formatCurrency(coverage.limit)}
                          </span>
                        </div>
                          {coverage.description && (
                            <TruncatedText
                              text={coverage.description}
                              maxLength={150}
                              className="mt-2.5 text-sm"
                            />
                          )}
                      </div>
                    ))}
                </div>
              </div>
              )}

              {/* Coverage Duration & Pricing */}
              {(isTravelInsurance || isTripSecureInsurance || isLifeInsurance || isGeneralInsurance) && (
              <div>
                <SectionHeader
                  icon={Clock}
                  title="Coverage Duration & Pricing"
                  subtitle="Your purchased coverage period and cost"
                    color={styles.color}
                />
                  <div className={`p-5 ${styles.bg} rounded-2xl border ${styles.border} shadow-sm`}>
                    <div className="flex justify-between items-center mb-5">
                      <div className="flex gap-3 items-center">
                        <div className={`grid place-items-center w-11 h-11 ${styles.bg} rounded-xl border ${styles.border}`}>
                          <Calendar size={20} className={styles.text} />
                        </div>
                        <div>
                          <h4 className={`text-base font-bold ${styles.text} mb-0.5`}>
                            {formatDurationLabel} Coverage
                          </h4>
                          <p className={`text-xs ${styles.text} opacity-80`}>
                            Active coverage period
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-3xl font-bold ${styles.text}`}>
                          {formatCurrency(getPrice)}
                        </span>
                        <p className={`text-xs ${styles.text} opacity-80 mt-1`}>Total Cost</p>
                      </div>
                    </div>

                    {/* Route Information for travel insurance / intercity_travel (not for travel_insurance with string duration) */}
                    {(isTravelInsurance || (isTripSecureInsurance && policyType === "intercity_travel")) && coverageDuration && !hasStringDuration && (
                      <div className="pt-4 border-t border-gray-200">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {coverageDuration.route_destination && (
                            <div className="p-3.5 bg-white rounded-xl border border-gray-100 shadow-sm">
                              <p className="mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Route Destination
                              </p>
                              <p className="text-sm font-bold text-gray-900">
                                {coverageDuration.route_destination}
                              </p>
                            </div>
                          )}
                          {coverageDuration.route_type && (
                            <div className="p-3.5 bg-white rounded-xl border border-gray-100 shadow-sm">
                              <p className="mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Route Type
                              </p>
                              <p className="text-sm font-bold text-gray-900 capitalize">
                                {coverageDuration.route_type}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
              )}

              {/* Package Details for travel_insurance with string duration */}
              {hasStringDuration && purchasedData && (
                <div>
                  <SectionHeader
                    icon={DollarSign}
                    title="Package Details"
                    subtitle="Your purchased package information"
                    color={styles.color}
                  />
                  <div className={`p-5 ${styles.bg} rounded-2xl border ${styles.border} shadow-sm`}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                        <p className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Product Name
                        </p>
                        <p className="text-base font-bold text-gray-900">
                          {purchasedData.product_name || "N/A"}
                        </p>
                      </div>
                      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                        <p className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Premium
                        </p>
                        <p className={`text-base font-bold ${styles.text}`}>
                          {formatCurrency(getPrice)}
                        </p>
                      </div>
                      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                        <p className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Duration
                        </p>
                        <p className="text-base font-bold text-gray-900">
                          {formatDurationLabel}
                        </p>
                      </div>
                    </div>
                    {purchasedData.description && (
                      <div className="mt-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                        <p className="mb-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Package Description
                        </p>
                        <TruncatedText
                          text={purchasedData.description}
                          maxLength={200}
                          className="text-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Trip Information (for travel insurance / intercity_travel) */}
              {isTravelInsurance && !hasStringDuration && purchasedData && (
                  <div>
                    <SectionHeader
                      icon={MapPin}
                      title="Trip Information"
                      subtitle="Journey details and vehicle information"
                    color={styles.color}
                    />
                  <div className={`p-6 space-y-4 ${styles.bg} rounded-xl border ${styles.border}`}>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {purchasedData.departureCity && (
                        <div>
                          <p className="mb-2 text-sm font-semibold text-gray-900">
                            Departure
                          </p>
                          <div className="p-3 bg-white rounded-lg">
                            <p className="font-semibold text-gray-900">
                              {purchasedData.departureCity}
                            </p>
                            <p className="text-sm text-gray-600">
                              {purchasedData.departureRegion}
                            </p>
                          </div>
                        </div>
                      )}
                      {purchasedData.destinationCity && (
                        <div>
                          <p className="mb-2 text-sm font-semibold text-gray-900">
                            Destination
                          </p>
                          <div className="p-3 bg-white rounded-lg">
                            <p className="font-semibold text-gray-900">
                              {purchasedData.destinationCity}
                            </p>
                            <p className="text-sm text-gray-600">
                              {purchasedData.destinationRegion}
                            </p>
                          </div>
                        </div>
                      )}
                      </div>
                    {(purchasedData.vehicleType || purchasedData.busLicensePlate) && (
                      <div>
                        <p className="mb-2 text-sm font-semibold text-gray-900">
                          Vehicle Details
                        </p>
                        <div className="p-3 bg-white rounded-lg">
                          {purchasedData.vehicleType && (
                          <div className="flex gap-2 items-center mb-2">
                              <Car size={16} className={styles.text} />
                            <span className="font-semibold text-gray-900">
                                {purchasedData.vehicleType}
                            </span>
                          </div>
                          )}
                          {purchasedData.busLicensePlate && (
                          <p className="text-sm text-gray-600">
                              License Plate: {purchasedData.busLicensePlate}
                          </p>
                          )}
                    </div>
                  </div>
                )}
                  </div>
                </div>
              )}

              {/* Bike / vehicle information (for okada_rider and daily_commuter) */}
              {isTripSecureInsurance && (policyType === "okada_rider" || policyType === "daily_commuter") && purchasedData && (
                <div>
                  <SectionHeader
                    icon={Car}
                    title={policyType === "daily_commuter" ? "Vehicle Information" : "Bike Information"}
                    subtitle={policyType === "daily_commuter" ? "Vehicle details" : "Motorcycle details"}
                    color={styles.color}
                  />
                  <div className={`p-5 space-y-4 ${styles.bg} rounded-2xl border ${styles.border} shadow-sm`}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {(purchasedData.vehicleName || purchasedData.bikeName) && (
                        <div>
                          <p className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            {policyType === "daily_commuter" ? "Vehicle Name/Model" : "Bike Name/Model"}
                          </p>
                          <div className="p-3.5 bg-white rounded-xl border border-gray-100 shadow-sm">
                            <p className="font-bold text-gray-900">
                              {purchasedData.vehicleName || purchasedData.bikeName}
                            </p>
                          </div>
                        </div>
                      )}
                      {purchasedData.licensePlate && (
                        <div>
                          <p className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            License Plate
                          </p>
                          <div className="p-3.5 bg-white rounded-xl border border-gray-100 shadow-sm">
                            <p className="font-mono font-bold text-gray-900">
                              {purchasedData.licensePlate}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Trip Information (for intercity_travel) */}
              {isTripSecureInsurance && policyType === "intercity_travel" && purchasedData && (
                  <div>
                    <SectionHeader
                      icon={MapPin}
                      title="Trip Information"
                      subtitle="Journey details and vehicle information"
                    color={styles.color}
                    />
                  <div className={`p-6 space-y-4 ${styles.bg} rounded-xl border ${styles.border}`}>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {purchasedData.departureCity && (
                        <div>
                          <p className="mb-2 text-sm font-semibold text-gray-900">
                            Departure
                          </p>
                          <div className="p-3 bg-white rounded-lg">
                            <p className="font-semibold text-gray-900">
                              {purchasedData.departureCity}
                            </p>
                            <p className="text-sm text-gray-600">
                              {purchasedData.departureRegion}
                            </p>
                          </div>
                        </div>
                      )}
                      {purchasedData.destinationCity && (
                        <div>
                          <p className="mb-2 text-sm font-semibold text-gray-900">
                            Destination
                          </p>
                          <div className="p-3 bg-white rounded-lg">
                            <p className="font-semibold text-gray-900">
                              {purchasedData.destinationCity}
                            </p>
                            <p className="text-sm text-gray-600">
                              {purchasedData.destinationRegion}
                            </p>
                          </div>
                        </div>
                      )}
                      </div>
                    {(purchasedData.vehicleType || purchasedData.busLicensePlate) && (
                      <div>
                        <p className="mb-2 text-sm font-semibold text-gray-900">
                          Vehicle Details
                        </p>
                        <div className="p-3 bg-white rounded-lg">
                          {purchasedData.vehicleType && (
                          <div className="flex gap-2 items-center mb-2">
                              <Car size={16} className={styles.text} />
                            <span className="font-semibold text-gray-900">
                                {purchasedData.vehicleType}
                            </span>
                          </div>
                          )}
                          {purchasedData.busLicensePlate && (
                          <p className="text-sm text-gray-600">
                              License Plate: {purchasedData.busLicensePlate}
                          </p>
                          )}
                    </div>
                  </div>
                )}
                  </div>
                </div>
              )}

              {/* Customer Information */}
              {customer && (customer.first_name || customer.email) && (
                <div>
                  <SectionHeader
                    icon={Users}
                    title="Customer Information"
                    subtitle="Policyholder details"
                    color={styles.color}
                  />
                  <div className={`p-5 space-y-4 ${styles.bg} rounded-2xl border ${styles.border} shadow-sm`}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {customer.first_name && (
                      <div>
                        <p className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Full Name
                        </p>
                        <div className="p-3.5 bg-white rounded-xl border border-gray-100 shadow-sm">
                            <p className="font-bold text-gray-900">
                              {customer.first_name} {customer.last_name || ""}
                            </p>
                          </div>
                        </div>
                      )}
                      {customer.email && (
                      <div>
                        <p className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Email
                        </p>
                        <div className="p-3.5 bg-white rounded-xl border border-gray-100 shadow-sm">
                            <p className="font-bold text-gray-900">
                              {customer.email}
                            </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

              {/* Eligibility & Exclusions */}
              {(((isLifeInsurance || isGeneralInsurance || hasStringDuration) 
                ? (purchasedData?.eligibility_criteria?.length > 0 || purchasedData?.exclusions?.length > 0)
                : (insurancePolicy.eligibility_criteria?.length > 0 || insurancePolicy.exclusions?.length > 0))) && (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {((isLifeInsurance || isGeneralInsurance || hasStringDuration) 
                    ? purchasedData?.eligibility_criteria?.length > 0 
                    : insurancePolicy.eligibility_criteria?.length > 0) && (
                <div>
                  <SectionHeader
                    icon={CheckCircle}
                    title="Eligibility Criteria"
                    subtitle="Requirements to qualify for this policy"
                        color={styles.color}
                  />
                  <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200/60 shadow-sm">
                    <ul className="space-y-2.5">
                          {((isLifeInsurance || isGeneralInsurance || hasStringDuration) 
                            ? purchasedData?.eligibility_criteria 
                            : insurancePolicy.eligibility_criteria)?.map(
                        (criteria, index) => (
                          <li
                            key={index}
                            className="flex gap-2.5 items-start text-sm text-green-800"
                          >
                            <CheckCircle
                              size={16}
                              className="text-green-600 mt-0.5 flex-shrink-0"
                            />
                                <span className="leading-relaxed">{criteria}</span>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
                  )}

                  {((isLifeInsurance || isGeneralInsurance || hasStringDuration) 
                    ? purchasedData?.exclusions?.length > 0 
                    : insurancePolicy.exclusions?.length > 0) && (
                <div>
                  <SectionHeader
                    icon={AlertTriangle}
                    title="Exclusions"
                    subtitle="What this policy does not cover"
                        color={styles.color}
                  />
                  <div className="p-4 bg-gradient-to-br from-red-50 to-rose-50 rounded-xl border border-red-200/60 shadow-sm">
                    <ul className="space-y-2.5">
                          {((isLifeInsurance || isGeneralInsurance || hasStringDuration) 
                            ? purchasedData?.exclusions 
                            : insurancePolicy.exclusions)?.map((exclusion, index) => (
                        <li
                          key={index}
                          className="flex gap-2.5 items-start text-sm text-red-800"
                        >
                          <AlertTriangle
                            size={16}
                            className="text-red-600 mt-0.5 flex-shrink-0"
                          />
                              <span className="leading-relaxed">{exclusion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                  )}
              </div>
              )}

              {/* Claim Process */}
              {((isLifeInsurance || isGeneralInsurance || hasStringDuration) 
                ? purchasedData?.claim_process_steps?.length > 0 
                : insurancePolicy.claim_process_steps?.length > 0) && (
                <div>
                  <SectionHeader
                    icon={FileText}
                    title="Claim Process"
                    subtitle="Steps to file a claim"
                    color={styles.color}
                  />
                  <div className="space-y-4">
                    {((isLifeInsurance || isGeneralInsurance || hasStringDuration) 
                      ? purchasedData?.claim_process_steps 
                      : insurancePolicy.claim_process_steps)?.map((step, index) => (
                      <div
                        key={index}
                        className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex gap-4 items-start">
                          <div className={`grid flex-shrink-0 place-items-center w-9 h-9 text-sm font-bold ${styles.text} ${styles.bg} rounded-xl border ${styles.border} shadow-sm`}>
                            {step.step_number}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="mb-1.5 font-bold text-gray-900">
                              {step.title}
                            </h4>
                            {step.description && (
                              <TruncatedText
                                text={step.description}
                                maxLength={150}
                                className="mb-3 text-sm"
                              />
                            )}
                            <div className="flex gap-4 items-center flex-wrap text-xs text-gray-600">
                              {step.timeframe && (
                              <span className="flex gap-1.5 items-center px-2.5 py-1 bg-gray-50 rounded-lg">
                                <Clock size={12} />
                                  {step.timeframe}
                              </span>
                              )}
                              {step.requirements && (
                              <span className="flex gap-1.5 items-center px-2.5 py-1 bg-gray-50 rounded-lg">
                                <FileText size={12} />
                                {step.requirements}
                              </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PolicyViewModal;
